import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Linkedin, ArrowRight, Plane, Ship, Truck, Warehouse, FileCheck, MessageCircle } from 'lucide-react';

const LOGO_URL = "/assets/elementary-cargo-logo-globe.png";
const LINKEDIN_URL = "https://www.linkedin.com/in/dayana-parra-diaz/";
const WHATSAPP_URL = "https://wa.me/31625381824";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  const services = [
    { name: t('nav.airFreight'), href: '/services/air-freight', icon: Plane },
    { name: t('nav.oceanFreight'), href: '/services/ocean-freight', icon: Ship },
    { name: t('nav.roadFreight'), href: '/services/road-freight', icon: Truck },
    { name: t('nav.warehousing'), href: '/services/warehousing', icon: Warehouse },
    { name: t('nav.customs'), href: '/services/customs', icon: FileCheck },
  ];

  const quickLinks = [
    { name: t('footer.links.aboutUs'), href: '/about' },
    { name: t('footer.links.ourServices'), href: '/services' },
    { name: t('sectors.title'), href: '/sectors' },
    { name: t('footer.links.trackShipment'), href: '/tracking' },
    { name: t('footer.links.requestQuote'), href: '/quote' },
    { name: t('footer.links.contact'), href: '/contact' },
  ];

  const legalLinks = [
    { name: t('footer.legal.privacy'), href: '/privacy' },
    { name: t('footer.legal.terms'), href: '/terms' },
    { name: t('footer.legal.cookies'), href: '/cookies' },
  ];

  return (
    <footer className="bg-navy-800 text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img
                src={LOGO_URL}
                alt="Elementary Cargo"
                className="h-16 w-auto object-contain"
              />
            </Link>
            <p className="text-steel-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded bg-navy-700 text-steel-400 hover:bg-gold hover:text-navy-800 transition-all duration-300"
                aria-label="Visit our LinkedIn profile"
                data-testid="footer-social-linkedin"
              >
                <Linkedin className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded bg-navy-700 text-steel-400 hover:bg-green-500 hover:text-white transition-all duration-300"
                aria-label="Contact us on WhatsApp"
                data-testid="footer-social-whatsapp"
              >
                <MessageCircle className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-gold font-outfit font-bold text-sm tracking-widest uppercase mb-6">
              {t('footer.ourServices')}
            </h4>
            <ul className="space-y-4">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="flex items-center gap-3 text-steel-400 hover:text-gold transition-colors group"
                    data-testid={`footer-service-${service.href.split('/').pop()}`}
                  >
                    <service.icon className="w-4 h-4 text-gold/50 group-hover:text-gold" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-gold font-outfit font-bold text-sm tracking-widest uppercase mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-steel-400 hover:text-gold transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.href.slice(1)}`}
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-gold font-outfit font-bold text-sm tracking-widest uppercase mb-6">
              {t('footer.contactUs')}
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-steel-400 hover:text-green-400 transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mt-1 text-green-500/70" />
                  <span>WhatsApp: +31 62 538 1824</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+31625381824"
                  className="flex items-start gap-3 text-steel-400 hover:text-gold transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 mt-1 text-gold/50" />
                  <span>+31 62 538 1824</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@elementarycargo.com"
                  className="flex items-start gap-3 text-steel-400 hover:text-gold transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mt-1 text-gold/50" />
                  <span>info@elementarycargo.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-steel-400">
                  <MapPin className="w-4 h-4 mt-1 text-gold/50 flex-shrink-0" />
                  <span>Based in The Netherlands</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Tagline */}
        <div className="mt-12 pt-8 border-t border-navy-700 text-center">
          <p className="text-gold font-space text-lg font-semibold tracking-wide">
            Moving with Purpose
          </p>
          {/* Company Registration Details */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <span className="text-steel-500 text-sm">KvK: 42041124</span>
            <span className="text-steel-600 hidden sm:inline">•</span>
            <span className="text-steel-500 text-sm">EORI: NL3785680245</span>
            <span className="text-steel-600 hidden sm:inline">•</span>
            <span className="text-steel-500 text-sm">VAT/BTW: NL005449408B56</span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-navy-700">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-steel-500 text-sm">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <div className="flex flex-wrap items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-steel-500 text-sm hover:text-gold transition-colors"
                data-testid={`footer-legal-${link.href.slice(1)}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
